import { defineAsyncComponent } from '/builds/gitlab/gitlab-ee/app/assets/javascripts/lib/utils/vue3compat/vue.js?vue3';
import { GlIcon, GlBadge, GlTooltip, GlTooltipDirective } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { VISIBILITY_TYPE_ICON, GROUP_VISIBILITY_TYPE } from '~/visibility_level/constants';
import { ACCESS_LEVEL_LABELS, ACCESS_LEVEL_NO_ACCESS_INTEGER } from '~/access_level/constants';
import { __ } from '~/locale';
import { numberToMetricPrefix, numberToHumanSize, isNumeric } from '~/lib/utils/number_utils';
import { TIMESTAMP_TYPES, TIMESTAMP_TYPE_CREATED_AT } from '~/vue_shared/components/resource_lists/constants';
import ListItem from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/resource_lists/list_item.vue?vue3';
import ListItemStat from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/resource_lists/list_item_stat.vue?vue3';
import GroupListItemActions from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/groups_list/group_list_item_actions.vue?vue3';
import ListItemInactiveBadge from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/resource_lists/list_item_inactive_badge.vue?vue3';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
export default {
    name: 'GroupsListItem',
    i18n: {
        subgroups: __('Subgroups'),
        projects: __('Projects'),
        directMembers: __('Direct members')
    },
    components: {
        ListItem,
        ListItemStat,
        GlIcon,
        GlBadge,
        GlTooltip,
        GroupListItemActions,
        ListItemInactiveBadge,
        GroupsListItemPlanBadge: defineAsyncComponent(()=>import('/builds/gitlab/gitlab-ee/ee/app/assets/javascripts/vue_shared/components/groups_list/groups_list_item_plan_badge.vue?vue3'))
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    mixins: [
        glSlotsMixin
    ],
    provide () {
        return {
            triggerDeleteLocation: 'list',
            triggerRestoreLocation: 'list'
        };
    },
    props: {
        group: {
            type: Object,
            required: true
        },
        showGroupIcon: {
            type: Boolean,
            required: false,
            default: false
        },
        listItemClass: {
            type: [
                String,
                Array,
                Object
            ],
            required: false,
            default: ''
        },
        timestampType: {
            type: String,
            required: false,
            default: TIMESTAMP_TYPE_CREATED_AT,
            validator (value) {
                return TIMESTAMP_TYPES.includes(value);
            }
        },
        includeMicrodata: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'click-avatar',
        'hover-stat',
        'hover-visibility',
        'refetch'
    ],
    computed: {
        visibility () {
            return this.group.visibility;
        },
        visibilityIcon () {
            return VISIBILITY_TYPE_ICON[this.visibility];
        },
        visibilityTooltip () {
            return GROUP_VISIBILITY_TYPE[this.visibility];
        },
        visibilityTooltipTarget () {
            return this.$refs?.visibilityIcon?.$el;
        },
        accessLevel () {
            return this.group.accessLevel?.integerValue;
        },
        accessLevelLabel () {
            return ACCESS_LEVEL_LABELS[this.accessLevel];
        },
        shouldShowAccessLevel () {
            const falsyValues = [
                undefined,
                null,
                ACCESS_LEVEL_NO_ACCESS_INTEGER
            ];
            return !falsyValues.includes(this.accessLevel);
        },
        groupIconName () {
            return this.group.parent ? 'subgroup' : 'group';
        },
        microdataAttributes () {
            if (!this.includeMicrodata) return {};
            return {
                itemprop: 'subOrganization',
                itemtype: 'https://schema.org/Organization',
                itemscope: true,
                avatarAttrs: {
                    itemprop: 'logo',
                    labelLinkAttrs: {
                        itemprop: 'name'
                    }
                },
                descriptionAttrs: {
                    itemprop: 'description'
                }
            };
        },
        descendantGroupsCount () {
            return numberToMetricPrefix(this.group.descendantGroupsCount);
        },
        projectsCount () {
            return this.group.projects?.count ?? this.group.projectsCount;
        },
        displayProjectsCount () {
            return numberToMetricPrefix(this.projectsCount);
        },
        groupMembersCount () {
            return numberToMetricPrefix(this.group.groupMembersCount);
        },
        showDescendantGroupsCount () {
            return isNumeric(this.group.descendantGroupsCount);
        },
        showProjectsCount () {
            return isNumeric(this.projectsCount);
        },
        showGroupMembersCount () {
            return isNumeric(this.group.groupMembersCount);
        },
        storageSize () {
            if (!this.hasStorageSize) {
                return null;
            }
            return numberToHumanSize(this.group.projectStatistics?.storageSize || 0);
        },
        hasStorageSize () {
            return Object.hasOwn(this.group, 'projectStatistics');
        },
        hasActions () {
            return this.group.availableActions?.length;
        },
        dataTestid () {
            return `groups-list-item-${this.group.id}`;
        }
    },
    methods: {
        refetch () {
            this.$emit('refetch');
        },
        onVisibilityTooltipShown () {
            this.$emit('hover-visibility', this.visibility);
        }
    }
};
